import { type LDContext, useIdentifyFeatureFlagUser } from '@modernloop/shared/feature-flag';

import type { UserInfo } from 'src/contexts/auth0/Auth0Context';

import { IS_PRODUCTION } from 'src/constants';

/**
 * Builds the LaunchDarkly evaluation context for a hiring-dashboard user.
 * Returns an anonymous context when no user is present, and strips PII in
 * production. Kept app-local because it depends on the app's `UserInfo` shape.
 *
 * A user with several Organization Memberships evaluates flags for whichever Organization is
 * active, so the active Organization is its own context kind and an Organization-targeted flag can
 * name it directly. That kind uses the attribute names of the backend `organization` context
 * (`organizationID`, `organizationName`, `organizationCreatedAt`), so one rule targets both sides.
 *
 * The `user` kind keeps `organizationID`, `organizationName` and `organizationCreatedAt` as well.
 * That duplication is deliberate (ADR 0008): production targeting rules read those attributes with
 * `contextKind: "user"`, so removing them would silently change how every one of those rules
 * evaluates.
 *
 * @param userInfo from useAuth();
 */
export function buildLaunchDarklyUser(userInfo: UserInfo | null): LDContext {
  if (!userInfo || !userInfo.userId) {
    return {
      kind: 'user',
      // Use the same key across all anonymous users
      key: 'anonymous-user-id',
      anonymous: true,
    };
  }

  const employeeName = userInfo?.employeeName || '';
  const ldName = employeeName ? `${employeeName} | ${userInfo.orgName}` : userInfo.orgName;
  const email = userInfo?.userEmail || undefined;

  const organizationAttributes: { [key: string]: string | number } = {};
  if (userInfo.orgId) {
    organizationAttributes.organizationID = userInfo.orgId;
  }
  if (userInfo.orgName) {
    organizationAttributes.organizationName = userInfo.orgName;
  }
  if (userInfo.orgCreatedAt) {
    organizationAttributes.organizationCreatedAt = userInfo.orgCreatedAt;
  }

  const userAttributes = {
    key: userInfo.userId,
    name: IS_PRODUCTION ? undefined : ldName,
    email: IS_PRODUCTION ? undefined : email,
    ...organizationAttributes,
  };

  if (!userInfo.orgId) {
    return { kind: 'user', ...userAttributes };
  }

  return {
    kind: 'multi',
    user: userAttributes,
    // The attribute names mirror the backend `organization` context in
    // LaunchDarklyFeatureFlagClient (ADR 0008), so one targeting rule matches on both sides.
    organization: {
      key: userInfo.orgId,
      name: userInfo.orgName,
      ...organizationAttributes,
    },
  };
}

/**
 * Identifies the hiring-dashboard user in LaunchDarkly via the shared
 * feature-flag identity hook.
 *
 * @param userInfo from useAuth();
 */
export const useIdentifyLaunchDarklyUser = (userInfo: UserInfo | null) => {
  useIdentifyFeatureFlagUser(buildLaunchDarklyUser(userInfo));
};
